/**
 * Quiz Results Page
 * Shows personalized university/program matches based on quiz answers
 */

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Sparkles, TrendingUp, MapPin, DollarSign, Award, ExternalLink } from 'lucide-react';
import { Button } from '@voilajsx/uikit/button';
import { useQuiz } from '../context/QuizContext';
import { SEO, Header, Footer } from '../../../shared/components';

const QuizResultsPage: React.FC = () => {
  const navigate = useNavigate();
  const { results, currentQuizType, isLoading } = useQuiz();

  useEffect(() => {
    if (!isLoading && !results) {
      navigate('/quiz');
    }
  }, [results, isLoading, navigate]);

  if (isLoading || !results) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="flex items-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
            <span className="text-foreground">Analyzing your preferences...</span>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const { matches, totalMatches } = results;

  return (
    <>
      <SEO
        title={`Your ${currentQuizType?.name} Results`}
        description={`Personalized university recommendations based on your preferences`}
      />

      <Header />

      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-primary via-primary to-emerald-700 text-white py-12">
          <div className="container mx-auto px-6">
            <div className="max-w-5xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-4">
                <Sparkles className="h-4 w-4" />
                <span className="text-sm font-medium">Your Personalized Results</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-extrabold mb-4 leading-tight">
                We Found {totalMatches} Perfect Matches
              </h1>
              <p className="text-lg md:text-xl text-white/90">
                Based on your {currentQuizType?.name.toLowerCase()} preferences
              </p>
            </div>
          </div>
        </div>

        {/* Results Grid */}
        <div className="container mx-auto px-6 py-10">
          <div className="max-w-6xl mx-auto">
            {/* Filters and Actions */}
            <div className="flex items-center justify-between mb-8">
              <div className="text-sm text-muted-foreground">
                Showing top {matches.length} matches sorted by compatibility
              </div>
              <Button
                variant="outline"
                onClick={() => navigate('/quiz')}
                className="border-2"
              >
                Take Another Quiz
              </Button>
            </div>

            {/* Matches List */}
            <div className="space-y-6">
              {matches.map((match, index) => (
                <div
                  key={`${match.university.id}-${match.program?.id || 'uni'}`}
                  className="bg-card border-2 border-border rounded-2xl p-6 hover:border-primary/50 hover:shadow-lg transition-all cursor-pointer"
                  onClick={() => {
                    if (match.program) {
                      navigate(`/program/${match.program.slug || match.program.id}`);
                    } else {
                      navigate(`/university/${match.university.slug || match.university.id}`);
                    }
                  }}
                >
                  {/* Match Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      {/* Rank Badge */}
                      <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-bold mb-3">
                        <Award className="h-4 w-4" />
                        #{index + 1} Match
                      </div>

                      {/* University/Program Name */}
                      {match.program ? (
                        <>
                          <h3 className="text-xl font-bold text-foreground mb-1">
                            {match.program.name}
                          </h3>
                          <p className="text-base text-muted-foreground">
                            {match.university.name}
                          </p>
                        </>
                      ) : (
                        <h3 className="text-2xl font-bold text-foreground">
                          {match.university.name}
                        </h3>
                      )}
                    </div>

                    {/* Match Score */}
                    <div className="text-right">
                      <div className="flex items-center gap-2 mb-1">
                        <TrendingUp className="h-5 w-5 text-primary" />
                        <span className="text-3xl font-extrabold text-primary">
                          {match.matchScore}%
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground uppercase tracking-wide">
                        Match Score
                      </span>
                    </div>
                  </div>

                  {/* University Details */}
                  <div className="grid md:grid-cols-3 gap-4 mb-4">
                    {/* Location */}
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <MapPin className="h-4 w-4 text-primary" />
                      <span>
                        {match.university.country.flag_emoji} {match.university.city}, {match.university.country.name}
                      </span>
                    </div>

                    {/* Ranking */}
                    {match.university.qs_world_ranking && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Award className="h-4 w-4 text-amber-500" />
                        <span>QS Rank #{match.university.qs_world_ranking}</span>
                      </div>
                    )}

                    {/* Tuition */}
                    {(match.program?.tuition_usd || match.university.tuition_usd_min) && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <DollarSign className="h-4 w-4 text-emerald-500" />
                        <span>
                          ${(match.program?.tuition_usd || match.university.tuition_usd_min)?.toLocaleString()}/year
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Match Factors */}
                  {match.matchFactors && match.matchFactors.length > 0 && (
                    <div className="bg-muted/50 rounded-xl p-4 mb-4">
                      <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">
                        Why this is a great match:
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {match.matchFactors.slice(0, 5).map((factor, idx) => (
                          <div
                            key={idx}
                            className="inline-flex items-center gap-1.5 bg-primary/10 text-primary px-3 py-1.5 rounded-lg text-sm font-medium"
                          >
                            <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                            {factor.factor}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* CTA */}
                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    <span className="text-sm text-muted-foreground">
                      Click to view full details
                    </span>
                    <ExternalLink className="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>
              ))}
            </div>

            {/* Empty State */}
            {matches.length === 0 && (
              <div className="text-center py-20">
                <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-6">
                  <Sparkles className="h-10 w-10 text-muted-foreground" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-2">
                  No matches found
                </h3>
                <p className="text-muted-foreground mb-6">
                  Try adjusting your preferences or take a different quiz
                </p>
                <Button onClick={() => navigate('/quiz')}>
                  Take Another Quiz
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default QuizResultsPage;
